import {useSelector} from "react-redux";

/**
 * Roles disponibles dans l'application
 */
export const ROLES = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    ADMIN_CLIENT: 'ADMIN_CLIENT',
    USER: 'USER'
};

/**
 * Fonction pure pour vérifier les droits d'édition sur un critère
 * @param {Object} currentUser - L'utilisateur courant
 * @param {Object} criterion - Le critère à vérifier (doit avoir user_id/creator et client_id)
 * @returns {boolean} - true si l'utilisateur peut modifier le critère
 * 
 * Structure attendue du critère:
 * - creator.id OU user_id : ID du créateur du critère
 * - client_id : ID du client (null = critère système)
 */
export const hasEditRightOnCriterion = (currentUser, criterion) => {
    if (!currentUser || !criterion) return false;
    
    const { user_id, created_by, client_id, creator, visibility_scope } = criterion;
    
    // Extraire l'ID du créateur - support plusieurs formats
    const creatorId = creator?.id || creator?.user_id || user_id || created_by;

    // 1. SUPER_ADMIN administre les critères système et ses critères privés.
    if (currentUser.role === ROLES.SUPER_ADMIN) {
        return true;
    }

    // Critères système: verrouillés côté UI pour les acteurs client.
    if (visibility_scope === 'SYSTEM' || client_id === null || client_id === undefined) {
        return false;
    }

    // 3. ADMIN_CLIENT peut modifier les critères de son client (ses utilisateurs)
    if (currentUser.role === ROLES.ADMIN_CLIENT) {
        return client_id === currentUser.client_id;
    }

    // 4. Utilisateur normal - seulement ses propres critères
    // Compare en string pour éviter les problèmes de type (int vs string)
    return String(creatorId) === String(currentUser.id);
};

/**
 * Hook pour obtenir une fonction de vérification des droits d'édition sur les critères
 * @returns {Function} canEditCriteria(criterion) - Fonction qui prend un critère et retourne si on peut l'éditer
 */
export const useCanEditCriteria = () => {
    const {currentUser} = useSelector(state => state.users);

    /**
     * @param {Object|number} criterionOrUserId - Le critère complet ou user_id (legacy)
     * @param {number} [client_id] - client_id (legacy, pour rétrocompatibilité)
     */
    const canEditCriteria = (criterionOrUserId, client_id) => {
        // Support legacy (user_id, client_id)
        if (typeof criterionOrUserId === 'number' || typeof criterionOrUserId === 'string') {
            return hasEditRightOnCriterion(currentUser, { 
                user_id: criterionOrUserId, 
                client_id 
            });
        }
        
        // Nouvelle API avec objet critère
        return hasEditRightOnCriterion(currentUser, criterionOrUserId);
    };

    return canEditCriteria;
};

/**
 * Hook pour vérifier si l'utilisateur peut créer des critères
 * @returns {boolean}
 */
export const useCanCreateCriteria = () => {
    const {currentUser} = useSelector(state => state.users);
    // Tous les utilisateurs connectés peuvent créer des critères
    return !!currentUser;
};
